import { Box, css, IconButton, styled, Tooltip } from "@mui/material";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useIsPhone } from "contexts/panelMode";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { useCanResetView } from "features/home/useCanResetView";
import { useResetView } from "features/home/useResetView";
import {
    selectIsRedoEmpty,
    selectIsUndoEmpty,
    selectPeekRedo,
    selectPeekUndo,
    undoRedoActions,
} from "features/undoRedo";
import ResetIcon from "media/icons/reset.svg?react";
import UndoIcon from "media/icons/undo.svg?react";
import { isMacintosh } from "utils/misc";

import {
    PROCORE_UNDO_GROUP,
    TOOLBAR_BUTTON,
    TOOLBAR_COLORS,
    TOOLBAR_CONTAINER,
    TOOLBAR_LAYOUT,
    TOOLBAR_Z_INDEX,
} from "../constants";

// =============================================================================
// Styled Components for Mobile Layout
// =============================================================================

const ToolbarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhone",
})<{ isPhone?: boolean }>`
    position: absolute;
    bottom: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-bottom, 0px)
    );
    right: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-right, 0px)
    );
    display: flex;
    align-items: center;
    height: ${PROCORE_UNDO_GROUP.HEIGHT}px;
    gap: ${PROCORE_UNDO_GROUP.GAP}px;
    z-index: ${TOOLBAR_Z_INDEX.TOOLBAR};
    pointer-events: none;

    & > * {
        flex-shrink: 0;
        pointer-events: auto;
    }
`;

const ButtonBox = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: ${PROCORE_UNDO_GROUP.BUTTON_WIDTH}px;
    height: ${PROCORE_UNDO_GROUP.BUTTON_HEIGHT}px;
    padding: ${TOOLBAR_CONTAINER.PADDING}px;
    background-color: ${TOOLBAR_CONTAINER.BG};
    border-radius: ${TOOLBAR_CONTAINER.RADIUS}px;
    border: ${TOOLBAR_CONTAINER.BORDER};
    box-shadow: ${TOOLBAR_CONTAINER.SHADOW};
`;

const ActionButton = styled(IconButton)<{ disabled?: boolean }>(({ disabled }) => {
    return css`
        width: 40px;
        height: 40px;
        padding: 0;
        border-radius: ${TOOLBAR_BUTTON.RADIUS}px;
        background-color: transparent;
        color: ${disabled ? TOOLBAR_COLORS.DISABLED : TOOLBAR_COLORS.DEFAULT};
        transition: background-color 0.15s ease;

        &:hover:not(:disabled) {
            background-color: ${TOOLBAR_COLORS.HOVER};
        }

        &:disabled {
            color: ${TOOLBAR_COLORS.DISABLED};
        }

        svg {
            width: 24px;
            height: 24px;
        }

        svg path {
            fill: currentColor;
        }
    `;
});

const ButtonLabel = styled("span")<{ disabled?: boolean }>(
    ({ disabled }) => css`
        font-size: 10px;
        font-weight: 500;
        color: ${disabled ? TOOLBAR_COLORS.DISABLED : TOOLBAR_COLORS.DEFAULT};
        text-align: center;
    `,
);

// =============================================================================
// Mobile Button Renderer (follows Toolbar pattern but with mobile styling)
// =============================================================================

interface MobileButtonRendererProps {
    toolbarId: string;
}

function MobileButtonRenderer({ toolbarId }: MobileButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (!state.visible) {
                    return null;
                }

                return (
                    <Tooltip
                        key={config.id}
                        title={config.tooltip ? `${config.title} (${config.tooltip.shortcutKey})` : config.title || ""}
                        placement="top"
                    >
                        <span>
                            <ButtonBox>
                                <ActionButton disabled={state.disabled} onClick={config.onClick}>
                                    {config.icon}
                                </ActionButton>
                                <ButtonLabel disabled={state.disabled}>{config.title}</ButtonLabel>
                            </ButtonBox>
                        </span>
                    </Tooltip>
                );
            })}
        </>
    );
}

// =============================================================================
// Main Component (follows ViewToolbar pattern)
// =============================================================================

interface MobileUndoRedoToolbarProps {
    id?: string;
}

/**
 * Mobile Undo/Redo Toolbar
 *
 * Follows the same context-based pattern as ViewToolbar/SettingsToolbar,
 * but uses mobile-specific styling (horizontal layout with labels).
 */
export function UndoRedoToolbar({ id = "mobile-undo-redo-toolbar" }: MobileUndoRedoToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const resetView = useResetView();

    const nextUndo = useAppSelector(selectPeekUndo);
    const nextRedo = useAppSelector(selectPeekRedo);
    const isUndoEmpty = useAppSelector(selectIsUndoEmpty);
    const isRedoEmpty = useAppSelector(selectIsRedoEmpty);
    const canReset = useCanResetView();
    const isPhone = useIsPhone();

    // Define buttons following the ToolbarButtonEntry pattern
    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: "reset",
                    icon: <ResetIcon />,
                    onClick: () => {
                        void resetView({ resetCamera: false, preservePicker: true });
                    },
                    title: "Reset",
                },
                state: {
                    active: false,
                    disabled: !canReset,
                    visible: true,
                },
            },
            {
                config: {
                    id: "undo",
                    icon: <UndoIcon />,
                    onClick: () => {
                        if (nextUndo) {
                            nextUndo.undoCallback();
                            dispatch(undoRedoActions.undo());
                        }
                    },
                    title: "Undo",
                    tooltip: {
                        toolName: "undo",
                        shortcutKey: isMacintosh ? "Cmd+Z" : "Ctrl+Z",
                        alwaysVisible: false,
                        placement: "top",
                    },
                },
                state: {
                    active: false,
                    disabled: isUndoEmpty,
                    visible: true,
                },
            },
            {
                config: {
                    id: "redo",
                    icon: <UndoIcon style={{ transform: "scaleX(-1)" }} />,
                    onClick: () => {
                        if (nextRedo) {
                            nextRedo.redoCallback();
                            dispatch(undoRedoActions.redo());
                        }
                    },
                    title: "Redo",
                    tooltip: {
                        toolName: "redo",
                        shortcutKey: isMacintosh ? "Cmd+Shift+Z" : "Ctrl+Y",
                        alwaysVisible: false,
                        placement: "top",
                    },
                },
                state: {
                    active: false,
                    disabled: isRedoEmpty,
                    visible: true,
                },
            },
        ],
        [dispatch, resetView, nextUndo, nextRedo, isUndoEmpty, isRedoEmpty, canReset],
    );

    // Register toolbar on mount (same pattern as ViewToolbar)
    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, buttons]);

    // Render with mobile-specific layout (instead of <Toolbar id={id} />)
    return (
        <ToolbarContainer isPhone={isPhone}>
            <MobileButtonRenderer toolbarId={id} />
        </ToolbarContainer>
    );
}
